import {
  AudioLinesIcon,
  BugIcon,
  CalendarIcon,
  CircleAlertIcon,
  InfoIcon,
  KanbanIcon,
  GitPullRequestIcon,
  HardDriveIcon,
  MailIcon,
  Table2Icon,
  TicketIcon,
  TriangleAlertIcon,
  VideoIcon,
  type LucideIcon,
} from "lucide-react"
import { useId, type ReactNode } from "react"
import { Badge } from "@/primitives/badge.tsx"
import { Skeleton } from "@/primitives/skeleton.tsx"
import { cn } from "@/primitives/utils"
import { Callout } from "./callout.tsx"
import { GoogleMark } from "./marks.tsx"
import { StatusDot } from "./status-dot.tsx"

/*
 * One account Beacon reaches, as a card: a Member's own on My Connectors, or Beacon's own
 * connection to a tracker on Trackers or to GitHub on GitHub. It says whose it is, whether it
 * works, what Beacon may do with it, and the one thing to do about it. On My Connectors it also
 * stands for an MCP connector in the catalog, before anything can be connected there.
 */

/**
 * A tile drawn in muted ink from what Beacon reaches there, since Beacon draws no tracker's, code
 * host's or MCP connector's brand: a ticket for a tracker, a pull request for GitHub, and for each
 * connector what its service holds.
 */
const mutedMark =
  (Icon: LucideIcon) =>
  ({ size }: { size: 16 | 20 }) => (
    <Icon
      aria-hidden
      className={cn("shrink-0 text-muted-foreground", size === 16 ? "size-4" : "size-5")}
    />
  )

/** Each account's mark, drawn in its own tile. */
const marks = {
  google: GoogleMark,
  jira: mutedMark(TicketIcon),
  github: mutedMark(GitPullRequestIcon),
  sentry: mutedMark(BugIcon),
  atlassian: mutedMark(KanbanIcon),
  "google-drive": mutedMark(HardDriveIcon),
  gmail: mutedMark(MailIcon),
  "google-calendar": mutedMark(CalendarIcon),
  airtable: mutedMark(Table2Icon),
  otter: mutedMark(AudioLinesIcon),
  zoom: mutedMark(VideoIcon),
} as const

/**
 * The connection's state as a dot and a word. A failed connection is a fault to notice, so it is
 * `feed-error`, never a warning. `unreachable` is a connection whose other end didn't answer as
 * expected, which may pass by itself; `failed` is one refused, which won't. `stopped` is a failed one
 * its holder can't reconnect, only disconnect, so it says no more than that it stopped. `expiring`
 * works now but its credential ends soon, which is something to act on before it stops: a warning.
 * An MCP connector that can't be connected yet is idle, as `none` is: `verifying` while Beacon
 * checks it against its live service, `unavailable` while it waits on something else. A Connector
 * account its owner `paused` is idle too: switched off on purpose, not a fault.
 */
const statuses = {
  connected: { tone: "success", label: "Connected" },
  expiring: { tone: "warning", label: "Expiring soon" },
  failed: { tone: "feed-error", label: "Needs reconnecting" },
  stopped: { tone: "feed-error", label: "Stopped working" },
  unreachable: { tone: "feed-error", label: "Not answering" },
  none: { tone: "idle", label: "Not connected" },
  verifying: { tone: "idle", label: "Being verified" },
  unavailable: { tone: "idle", label: "Not available yet" },
  paused: { tone: "idle", label: "Paused" },
} as const

/** The statuses with nothing connected, whose card still says what connecting would take. */
const unconnected = new Set<keyof typeof statuses>(["none", "verifying", "unavailable"])

/**
 * `status` is where the connection stands: `none` until the Member connects, or for an MCP
 * connector that can't be connected yet, `verifying` or `unavailable`. `meta` is the page's sentence
 * about it ("Connected since 22 September 2026"), and `scope` what Beacon may read ("Feed setup ·
 * Read-only Drive"). `promise`, what connecting hands over, or for an MCP connector why it can't be
 * connected yet, is shown only while nothing is connected. `action` is the card's one control,
 * Connect or Reconnect, or nothing when there is nothing to do here; `more` is its RowActions,
 * holding Disconnect, after the action on the desk.
 * `failure`, the sentence for why a failed, stopped or unreachable connection stopped, is set in a
 * `feed-error` Callout across the card's foot.
 * `warning`, the sentence for what to act on before a connection stops, such as a credential
 * expiring, is set in a `warning` Callout there instead, on any connection that hasn't failed.
 * `note`, a fact about the card worth knowing whatever its status, such as that it is paused or what
 * became of an account disconnected from it, is set in a `neutral` Callout there, under either.
 * On `compact` the action wraps under the text, in line with it, and `more` stands at the card's
 * top right, beside the title, so a ⋯ never has a line to itself; only the title gives way to it.
 */
export function IntegrationCard({
  mark,
  title,
  status,
  meta,
  scope,
  promise,
  action,
  more,
  failure,
  warning,
  note,
}: {
  mark: keyof typeof marks
  title: string
  status: keyof typeof statuses
  meta?: string
  scope: string
  promise?: string
  action: ReactNode
  more?: ReactNode
  failure?: string
  warning?: string
  note?: string
}) {
  const heading = useId()
  const Mark = marks[mark]
  const { tone, label } = statuses[status]
  return (
    <section
      aria-labelledby={heading}
      className="space-y-4 rounded-lg bg-card p-5 shadow-raised ring-1 ring-border compact:relative"
    >
      <div className="flex gap-4 compact:flex-wrap">
        <span
          aria-hidden
          className="grid size-10 shrink-0 place-items-center rounded-lg bg-surface-sunken ring-1 ring-border-subtle"
        >
          <Mark size={20} />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          {/* On `compact` the ⋯ stands over this line's end, so the title leaves it room. */}
          <h2 id={heading} className={cn("text-title font-semibold", more && "compact:pr-8")}>
            {title}
          </h2>
          <StatusDot tone={tone} label={label} />
          {meta && <p className="text-xs text-muted-foreground">{meta}</p>}
          <div className="pt-1">
            <Badge variant="neutral">{scope}</Badge>
          </div>
          {unconnected.has(status) && promise && (
            <p className="max-w-prose pt-1 text-ui text-muted-foreground">{promise}</p>
          )}
        </div>
        {/* On `compact` this box gives way, so its action and `more` place themselves in the card:
            `more` at its top right, over the title's line alone, so the lines under it keep their
            width. */}
        <div className="flex shrink-0 items-start compact:contents">
          <div className="flex items-center gap-2 compact:contents">
            <div
              className={cn(
                "flex shrink-0 items-start compact:order-last compact:basis-full compact:pl-14",
                !action && "compact:hidden",
              )}
            >
              {action}
            </div>
            {more && (
              <div className="flex shrink-0 items-start compact:absolute compact:top-4 compact:right-4">
                {more}
              </div>
            )}
          </div>
        </div>
      </div>
      {(status === "failed" || status === "stopped" || status === "unreachable") && failure ? (
        <Callout tone="feed-error" icon={CircleAlertIcon}>
          {failure}
        </Callout>
      ) : (
        !unconnected.has(status) &&
        warning && (
          <Callout tone="warning" icon={TriangleAlertIcon}>
            {warning}
          </Callout>
        )
      )}
      {note && (
        <Callout tone="neutral" icon={InfoIcon}>
          {note}
        </Callout>
      )}
    </section>
  )
}

/**
 * Where a connection stands, as its dot and word alone, away from its card: Home's Google Drive
 * card, which leads to Connections, says it the same way the card there does.
 */
export function ConnectionStatus({ status }: { status: keyof typeof statuses }) {
  const { tone, label } = statuses[status]
  return <StatusDot tone={tone} label={label} />
}

/**
 * An IntegrationCard while the Member's connections load: the same card, its mark, lines and button
 * in grey at their real places, so the page doesn't move as it arrives: on `compact`, the button's
 * under the text, as the card's action is.
 */
export function IntegrationCardSkeleton() {
  return (
    <div
      role="status"
      aria-busy
      aria-label="Loading"
      className="flex gap-4 rounded-lg bg-card p-5 shadow-raised ring-1 ring-border compact:flex-wrap"
    >
      <Skeleton className="size-10 shrink-0 rounded-lg" />
      <div className="flex-1 space-y-2 pt-0.5">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-52" />
        <Skeleton className="h-5 w-28" />
      </div>
      <div className="shrink-0 compact:basis-full compact:pl-14">
        <Skeleton className="h-7 w-32" />
      </div>
    </div>
  )
}
